import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  Optional,
  OutputEmitterRef,
  Signal,
  computed,
  input,
  output,
} from '@angular/core';
import { isEqual } from 'lodash';

import { InsightsBaseEvent, InsightsEventsTrackingService, insightsNamedEvent } from '@core/shared/util';
import {
  FilterSelectionOption,
  MultiSelectionGroupComponent,
  SearchFieldComponent,
  SingleSelectionGroupComponent,
  ToggleFilterComponent,
} from '@core/ui';
import { ArticleExportStateType, ExportJobProvider } from '@mp/content-manager/exports/domain';
import { ExportHistoryInsightsEvent } from '@mp/content-manager/exports/util';

import { ExportsFilter } from '../../models';

import { ARTICLE_EXPORT_STATE_FILTER_OPTIONS } from './article-export-state-filter-options';

@Component({
  selector: 'mpcm-exports-filter',
  templateUrl: './exports-filter.component.html',
  styleUrl: './exports-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SearchFieldComponent, ToggleFilterComponent, SingleSelectionGroupComponent, MultiSelectionGroupComponent],
})
export class ExportsFilterComponent {
  readonly exportsFilter: InputSignal<ExportsFilter | undefined> = input<ExportsFilter | undefined>();

  readonly exportsJobProviders: InputSignal<readonly ExportJobProvider[] | undefined> = input<
    readonly ExportJobProvider[] | undefined
  >();

  readonly exportsFilterChange: OutputEmitterRef<ExportsFilter> = output<ExportsFilter>();

  protected readonly currentFilter: Signal<ExportsFilter> = computed(
    () => this.exportsFilter() ?? { stateType: '', exportTypes: [], searchTerm: '', warningsOnly: false },
  );

  protected readonly exportTypeFilterOptions: Signal<FilterSelectionOption<string>[]> = computed(() =>
    (this.exportsJobProviders() ?? []).map(({ exportType, displayName }) => ({
      value: exportType,
      label: displayName,
    })),
  );

  readonly stateTypeFilterOptions: FilterSelectionOption<ArticleExportStateType | ''>[] =
    ARTICLE_EXPORT_STATE_FILTER_OPTIONS;

  constructor(@Optional() private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null) {}

  handleWarningStateFilterChange(isWarningStateFilterActive: boolean): void {
    const isWarningsOnlyEnabledEvent: ExportHistoryInsightsEvent = isWarningStateFilterActive
      ? ExportHistoryInsightsEvent.ENABLE_SHOW_WARNINGS_ONLY
      : ExportHistoryInsightsEvent.DISABLE_SHOW_WARNINGS_ONLY;
    this.insightsEventsTrackingService?.trackEvent(isWarningsOnlyEnabledEvent);

    this.updateFilter({ warningsOnly: isWarningStateFilterActive });
  }

  handleStatusFilterChange(stateType: ArticleExportStateType | ''): void {
    this.updateFilter({ stateType: stateType });
  }

  handleExportTypesFilterChange(exportTypes: string[]): void {
    this.updateFilter({ exportTypes: exportTypes });
  }

  onSearchbarSubmitted(searchTerm: string): void {
    this.insightsEventsTrackingService?.trackEvent(ExportHistoryInsightsEvent.PERFORM_SEARCH);
    this.updateFilter({ searchTerm });
  }

  onSearchbarClear(): void {
    this.insightsEventsTrackingService?.trackEvent(ExportHistoryInsightsEvent.CLEAR_SEARCH_TERM);
    this.updateFilter({ searchTerm: '' });
  }

  private updateFilter(filterChange: Partial<ExportsFilter>): void {
    const updatedFilter: ExportsFilter = { ...this.currentFilter(), ...filterChange };

    if (isEqual(this.currentFilter(), updatedFilter)) {
      return;
    }

    this.exportsFilterChange.emit(updatedFilter);
  }

  trackFilterExpansionChange(isExpanded: boolean, filterName: string): void {
    const filtersExpansionEvent: InsightsBaseEvent = isExpanded
      ? InsightsBaseEvent.FILTER_EXPAND
      : InsightsBaseEvent.FILTER_COLLAPSE;
    this.insightsEventsTrackingService?.trackEvent(insightsNamedEvent(filterName, filtersExpansionEvent));
  }

  trackFilterValueSelect(valueLabel: string, filterName: string): void {
    this.insightsEventsTrackingService?.trackEvent(insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_CHANGE), {
      value: valueLabel,
    });
  }
}
